import React, { Dispatch } from 'react';
import { IKeyTable, useKeyTable } from '../API/GoMoto';
import { isElectron } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import { useHardwareProviderState } from './hardware';
import { useKioskState } from './Kiosk/kiosk';
import { useMZeroState } from './mzero';
import { isDevelopment } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import { ActionMap } from '../API/GoMoto.types';

export type LockerStatus = 'Available' | 'Taken';
export type DoorStatus = 'Open' | 'Closed';

export interface ILockerFixture {
    Id: number;
    Status: LockerStatus;
    DoorStatus?: DoorStatus;
}

export enum LockerTypes {
    Set = 'SET',
    Update = 'UPDATE',
    Error = 'ERROR',
    Loading = 'LOADING'
}

type LockerPayload = {
    [LockerTypes.Set]: ILockerFixture[];
    [LockerTypes.Update]: ILockerFixture;
    [LockerTypes.Error]: string;
    [LockerTypes.Loading]: boolean;
};

type LockerActions = ActionMap<LockerPayload>[keyof ActionMap<LockerPayload>];
// While the locker context gets access to all actions, we only want to expose a subset externally.
type LockerActionsRestricted = ActionMap<Pick<LockerPayload, LockerTypes.Update>>[keyof ActionMap<Pick<LockerPayload, LockerTypes.Update>>];
type LockerState = { lockers: ILockerFixture[]; loading: boolean; error?: string; };

const initialLockerState: LockerState = { lockers: [],
    loading: false };
const LockerContext = React.createContext<LockerState>(initialLockerState);
const LockerDispatchContext = React.createContext<Dispatch<LockerActionsRestricted>>(() => null);

export const useLockerContext = () => React.useContext(LockerContext);
const useLockerDispatchContext = () => React.useContext(LockerDispatchContext);

export const lockerReducer = (state: LockerState, action: LockerActions) => {
    switch (action.type) {
        case LockerTypes.Set:
            return { lockers: [...action.payload.sort((a, b) => a.Id - b.Id)],
                loading: false };
        case LockerTypes.Update:
            const existing = state.lockers.findIndex((s) => s.Id === action.payload.Id);

            if (existing > -1) {
                return { ...state,
                    lockers: [...state.lockers.filter((_, index) => index !== existing), action.payload] };
            }
            else {
                return state;
            }
        case LockerTypes.Error:
            return { ...state,
                error: action.payload,
                loading: false };
        case LockerTypes.Loading:
            if (action.payload) {
                return { ...state,
                    lockers: [],
                    error: undefined,
                    loading: action.payload };
            }
            else {
                return { ...state,
                    loading: action.payload };
            }
    }
};

export const useSetLockerStatus = () => {
    const dispatch = useLockerDispatchContext();

    return (locker: ILockerFixture, status: ILockerFixture['Status']) => {
        if (locker.Status !== status) {
            const updated = {
                ...locker,
                Status: status,
            } as typeof locker;

            dispatch({ type: LockerTypes.Update,
                payload: updated });
        }
    };
};

export const Lockers = ({ children }: { children: React.ReactNode; }) => (isElectron() ? <LockerProvider>{children}</LockerProvider> : <>{children}</>);

const LockerProvider = ({ children }: { children: React.ReactNode; }) => {
    const { mzero, connected, lockersEnabled } = useMZeroState();
    const { isLinux } = useHardwareProviderState();
    const { key, keyStorageType } = useKioskState();
    const keyTable = useKeyTable();

    const [state, dispatch] = React.useReducer(lockerReducer, initialLockerState, () => ({
        lockers: [],
        loading: false,
    }));

    React.useEffect(() => {
        if (!state.lockers.length && !state.loading && (isDevelopment() || (connected && lockersEnabled) || isLinux)) {
            dispatch({ type: LockerTypes.Loading,
                payload: true });
            if (isDevelopment()) {
                /*
                 * const devLockers = Array.from(Array(20).keys()).map<ILockerFixture>((i) => ({
                 *  Id: i + 1,
                 *  Alias: '',
                 *  DoorStatus: '',
                 *  Status: 'Available',
                 *  DoorStatuses: { Front: 'Closed' },
                 *  DoorIds: { Front: 1 },
                 *  Features: {
                 *      Size: 'Small',
                 *      Accessible: 'Standard',
                 *      Status: 'Available',
                 *      Active: 'True'
                 *  }
                 * }));
                 *
                 * dispatch({ type: LockerTypes.Set, payload: devLockers });
                 */
                if (key && !!keyStorageType) {
                    keyTable.takenKeySlots()
                        .then((keyslots: IKeyTable[]) => {
                            const lockers: ILockerFixture[] = [];

                            for (let index = 1; index <= 20; index++) {
                                lockers.push({
                                    Id: index,
                                    Status: keyslots.findIndex((locker) => locker.keySlotNumber === index) > -1 ? 'Taken' : 'Available',
                                });
                            }

                            dispatch({ type: LockerTypes.Set,
                                payload: lockers });
                        });
                }
            }
            else if (isLinux) {
                if (key && !!keyStorageType) {
                    keyTable.takenKeySlots()
                        .then((keyslots: IKeyTable[]) => {
                            const lockers: ILockerFixture[] = [];

                            // Outdoor and express kiosks always have exactly 16 lockers.
                            for (let index = 1; index <= 16; index++) {
                                lockers.push({
                                    Id: index,
                                    Status: keyslots.findIndex((locker) => locker.keySlotNumber === index) > -1 ? 'Taken' : 'Available',
                                });
                            }

                            dispatch({ type: LockerTypes.Set,
                                payload: lockers });
                        });
                }
            }
            else if (lockersEnabled) {
                mzero?.create.locker.checkLockers()
                    .start({
                        Done: (err, data) => {
                            if (err) {
                                dispatch({ type: LockerTypes.Error,
                                    payload: err });
                            }

                            dispatch({ type: LockerTypes.Set,
                                payload: data.Lockers ?? [] });
                        },
                    });
            }
        }
    }, [state, mzero, connected, lockersEnabled, key, isLinux, keyTable, keyStorageType]);

    return (
        <LockerContext.Provider value={state}>
            <LockerDispatchContext.Provider value={dispatch}>{children}</LockerDispatchContext.Provider>
        </LockerContext.Provider>
    );
};
